<template>
  <v-sheet height="100%" color="bg1" width="100%">
    <!-- Edit Release Notes Dialog -->
    <v-dialog v-model="editDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="h6 py-2">
          Edit Release Notes - v{{ editForm.version }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="mt-4">
          <v-textarea
            v-model="editForm.releaseNotes"
            label="Release Notes"
            placeholder="Describe what's in this version..."
            outlined
            dense
            rows="5"
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditDialog" :disabled="isUpdating">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="updateReleaseNotes"
            :loading="isUpdating"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Upload Dialog -->
    <v-dialog v-model="uploadDialog" persistent max-width="500">
      <v-card>
        <v-card-title class="h6 py-2">Upload New Version</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="mt-4">
          <v-form ref="uploadForm" v-model="uploadFormValid">
            <v-file-input
              v-model="uploadForm.file"
              label="Select .exe file"
              accept=".exe"
              prepend-icon="mdi-file-upload"
              outlined
              dense
              :rules="[rules.required, rules.exeFile]"
              show-size
            ></v-file-input>

            <v-text-field
              v-model="uploadForm.version"
              label="Version"
              placeholder="e.g., 1.2.0"
              outlined
              dense
              :rules="[rules.required]"
            ></v-text-field>

            <v-textarea
              v-model="uploadForm.releaseNotes"
              label="Release Notes"
              placeholder="Describe what's new in this version..."
              outlined
              dense
              rows="3"
            ></v-textarea>

            <v-checkbox
              v-model="uploadForm.isLatest"
              label="Mark as Latest Version"
              hint="This will unset the current latest version"
              persistent-hint
              class="mt-0"
            ></v-checkbox>

            <v-checkbox
              v-model="uploadForm.isActive"
              label="Active (available for download)"
              class="mt-0"
            ></v-checkbox>
          </v-form>

          <!-- Upload Progress -->
          <div v-if="isUploading" class="mt-4">
            <div class="text-caption mb-1">Uploading... {{ uploadProgress }}%</div>
            <v-progress-linear
              :value="uploadProgress"
              color="primary"
              height="8"
              rounded
            ></v-progress-linear>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeUploadDialog" :disabled="isUploading">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="uploadVersion"
            :disabled="!uploadFormValid || isUploading"
            :loading="isUploading"
          >
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-sheet height="100%" class="rounded-tl-lg overflow-hidden pa-4 d-flex flex-column">
      <div
        class="pa-2 d-flex align-center justify-space-between"
        style="gap: 8px"
        :class="{ 'flex-wrap': $vuetify.breakpoint.xs }"
      >
        <div class="d-flex align-center flex-wrap">
          <h3 class="me-3">Point Cloud Utility Versions</h3>
          <v-sheet :width="$vuetify.breakpoint.xs ? '100%' : 250">
            <v-text-field
              hide-details
              outlined
              dense
              v-model="searchTxt"
              class="search-bar"
              placeholder="Search..."
              clearable
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
          </v-sheet>
        </div>

        <div class="d-flex align-center" style="gap: 0.75rem">
          <v-btn
            color="primary"
            @click="openUploadDialog"
          >
            <v-icon left>mdi-upload</v-icon>
            Upload Version
          </v-btn>
        </div>
      </div>

      <v-data-table
        class="elevation-0 pa-2 rounded-0 d-flex flex-column table-sticky custom-table flex-grow-1"
        :headers="headers"
        :search="searchTxt"
        :items="versions"
        :loading="isLoading"
      >
        <!-- Version Column -->
        <template v-slot:[`item.version`]="{ item }">
          <b>{{ item.version }}</b>
        </template>

        <!-- File Size Column -->
        <template v-slot:[`item.fileSize`]="{ item }">
          {{ formatFileSize(item.fileSize) }}
        </template>

        <!-- Release Notes Column -->
        <template v-slot:[`item.releaseNotes`]="{ item }">
          <v-tooltip bottom max-width="300" v-if="item.releaseNotes">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="text-truncate d-inline-block" style="max-width: 200px">
                {{ item.releaseNotes }}
              </span>
            </template>
            <span style="white-space: pre-wrap">{{ item.releaseNotes }}</span>
          </v-tooltip>
          <span v-else class="text--disabled">-</span>
        </template>

        <!-- Latest Badge Column -->
        <template v-slot:[`item.isLatest`]="{ item }">
          <div class="d-flex justify-center">
            <v-chip
              v-if="item.isLatest"
              small
              color="primary"
            >
              <v-icon small left>mdi-star</v-icon>
              Latest
            </v-chip>
            <v-btn
              v-else
              x-small
              text
              color="grey"
              @click="setAsLatest(item)"
            >
              Set Latest
            </v-btn>
          </div>
        </template>

        <!-- Active Toggle Column -->
        <template v-slot:[`item.isActive`]="{ item }">
          <div class="d-flex align-center justify-center">
            <v-chip
              :color="item.isActive ? 'success' : 'grey'"
              :outlined="!item.isActive"
              small
              class="mr-2"
            >
              {{ item.isActive ? 'Active' : 'Inactive' }}
            </v-chip>
            <v-switch
              :input-value="item.isActive"
              @change="toggleActive(item)"
              hide-details
              dense
              class="mt-0 pt-0 ml-1"
            ></v-switch>
          </div>
        </template>

        <!-- Created Date Column -->
        <template v-slot:[`item.createdOn`]="{ item }">
          {{ $formatDate(item.createdOn) }}
        </template>

        <!-- Actions Column -->
        <template v-slot:[`item.actions`]="{ item }">
          <v-menu dense offset-x right>
            <template v-slot:activator="{ attrs, on }">
              <v-btn icon elevation="0" v-bind="attrs" v-on="on">
                <v-icon size="16px">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list class="more-options-menu" dense>
              <v-list-item
                @click.stop="downloadVersion(item)"
                dense
              >
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="primary">mdi-download</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Download</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                @click.stop="openEditDialog(item)"
                dense
              >
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="success">mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Edit Release Notes</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                @click.stop="deleteVersion(item)"
                dense
              >
                <v-list-item-icon class="mr-2 justify-center">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:no-data>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">No versions uploaded yet</p>
          </div>
        </template>

        <template v-slot:loading>
          <div class="text-center d-flex flex-column">
            <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
            <p class="font-weight-medium">Loading versions...</p>
          </div>
        </template>
      </v-data-table>
    </v-sheet>
  </v-sheet>
</template>

<script>
import { $has, $formatDate } from '@/utils';
import { downloadURI } from '@/scripts/js/forgeService';
import permissions from '@Share/config/permissions';
import headers from '../config/pointcloudutility.headers';
import pointCloudUtilityService from '../services/pointcloudutility.service';

export default {
  name: 'PointCloudUtilityManagement',
  data() {
    return {
      permissions,
      headers,
      searchTxt: '',
      versions: [],
      isLoading: false,

      // Upload dialog
      uploadDialog: false,
      uploadFormValid: false,
      isUploading: false,
      uploadProgress: 0,
      uploadForm: {
        file: null,
        version: '',
        releaseNotes: '',
        isLatest: true,
        isActive: true,
      },
      rules: {
        required: (v) => !!v || 'This field is required',
        exeFile: (v) => !v || v.name?.toLowerCase().endsWith('.exe') || 'Only .exe files are allowed',
      },

      // Edit dialog
      editDialog: false,
      isUpdating: false,
      editForm: {
        id: null,
        version: '',
        releaseNotes: '',
      },
    };
  },
  mounted() {
    this.loadVersions();
  },
  methods: {
    $has,
    $formatDate,

    async loadVersions() {
      this.isLoading = true;
      try {
        const response = await pointCloudUtilityService.getVersions();
        this.versions = response.data;
      } catch (err) {
        this.$dialog.notify.error(err?.response?.data?.message || 'Failed to load versions', {
          position: 'top-right',
          timeout: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '-';
      const mb = bytes / (1024 * 1024);
      return `${mb.toFixed(1)} MB`;
    },

    openUploadDialog() {
      this.uploadForm = {
        file: null,
        version: '',
        releaseNotes: '',
        isLatest: true,
        isActive: true,
      };
      this.uploadProgress = 0;
      this.uploadDialog = true;
    },

    closeUploadDialog() {
      this.uploadDialog = false;
      this.uploadForm = {
        file: null,
        version: '',
        releaseNotes: '',
        isLatest: true,
        isActive: true,
      };
    },

    async uploadVersion() {
      if (!this.$refs.uploadForm.validate()) return;

      const formData = new FormData();
      formData.append('File', this.uploadForm.file);
      formData.append('Version', this.uploadForm.version);
      formData.append('ReleaseNotes', this.uploadForm.releaseNotes || '');
      formData.append('IsLatest', this.uploadForm.isLatest.toString());
      formData.append('IsActive', this.uploadForm.isActive.toString());

      this.isUploading = true;
      this.uploadProgress = 0;

      try {
        await pointCloudUtilityService.upload(formData, (progressEvent) => {
          this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        });

        this.$dialog.notify.success('Version uploaded successfully', {
          position: 'top-right',
          timeout: 3000,
        });

        this.closeUploadDialog();
        await this.loadVersions();
      } catch (err) {
        this.$dialog.notify.error(err?.response?.data?.message || 'Failed to upload version', {
          position: 'top-right',
          timeout: 3000,
        });
      } finally {
        this.isUploading = false;
      }
    },

    async toggleActive(item) {
      try {
        const response = await pointCloudUtilityService.toggleActive(item.id);
        const index = this.versions.findIndex((v) => v.id === item.id);
        if (index !== -1) {
          this.$set(this.versions, index, response.data);
        }
        this.$dialog.notify.success(
          `Version ${item.version} is now ${response.data.isActive ? 'active' : 'inactive'}`,
          { position: 'top-right', timeout: 3000 }
        );
      } catch (err) {
        this.$dialog.notify.error(err?.response?.data?.message || 'Failed to toggle status', {
          position: 'top-right',
          timeout: 3000,
        });
      }
    },

    setAsLatest(item) {
      this.$dialog.warning({
        text: `Set version ${item.version} as the latest? This will unset the current latest version.`,
        title: 'Set as Latest',
        actions: {
          false: 'Cancel',
          true: {
            text: 'Confirm',
            color: 'primary',
            handle: async () => {
              try {
                await pointCloudUtilityService.setLatest(item.id);
                await this.loadVersions();
                this.$dialog.notify.success(`Version ${item.version} is now the latest`, {
                  position: 'top-right',
                  timeout: 3000,
                });
              } catch (err) {
                this.$dialog.notify.error(err?.response?.data?.message || 'Failed to set as latest', {
                  position: 'top-right',
                  timeout: 3000,
                });
              }
            },
          },
        },
      });
    },

    openEditDialog(item) {
      this.editForm = {
        id: item.id,
        version: item.version,
        releaseNotes: item.releaseNotes || '',
      };
      this.editDialog = true;
    },

    closeEditDialog() {
      this.editDialog = false;
      this.editForm = {
        id: null,
        version: '',
        releaseNotes: '',
      };
    },

    async downloadVersion(item) {
      try {
        const response = await pointCloudUtilityService.getDownloadUrl(item.id);
        const { downloadUrl } = response.data;
        downloadURI(downloadUrl, item.fileName || `PointCloudUtility-v${item.version}.exe`);
      } catch (err) {
        this.$dialog.notify.error(err?.response?.data?.message || 'Failed to get download URL', {
          position: 'top-right',
          timeout: 3000,
        });
      }
    },

    async updateReleaseNotes() {
      this.isUpdating = true;
      try {
        const response = await pointCloudUtilityService.updateReleaseNotes(
          this.editForm.id,
          this.editForm.releaseNotes
        );

        const index = this.versions.findIndex((v) => v.id === this.editForm.id);
        if (index !== -1) {
          this.$set(this.versions, index, response.data);
        }

        this.$dialog.notify.success('Release notes updated successfully', {
          position: 'top-right',
          timeout: 3000,
        });

        this.closeEditDialog();
      } catch (err) {
        this.$dialog.notify.error(err?.response?.data?.message || 'Failed to update release notes', {
          position: 'top-right',
          timeout: 3000,
        });
      } finally {
        this.isUpdating = false;
      }
    },

    deleteVersion(item) {
      this.$dialog.error({
        text: `Are you sure you want to delete version ${item.version}?`,
        title: 'Delete Version',
        actions: {
          false: 'Cancel',
          true: {
            text: 'Delete',
            color: 'error',
            handle: async () => {
              try {
                await pointCloudUtilityService.delete(item.id);
                this.versions = this.versions.filter((v) => v.id !== item.id);
                this.$dialog.notify.success('Version deleted successfully', {
                  position: 'top-right',
                  timeout: 3000,
                });
              } catch (err) {
                this.$dialog.notify.error(err?.response?.data?.message || 'Failed to delete version', {
                  position: 'top-right',
                  timeout: 3000,
                });
              }
            },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: default;
}
</style>
